/**
 * helpers.js — Pure utility functions for the Quote buy-flow.
 * No React, no side-effects, just data transformations.
 */
import { PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

/* ========================== Date helpers ========================== */

/** Today as 'YYYY-MM-DD' in the local timezone. */
export function todayStr() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

/** Calculate age from a 'YYYY-MM-DD' date-of-birth string. */
export function ageFromDob(dobString) {
  if (!dobString) return null;
  const parts = dobString.split('-');
  if (parts.length !== 3) return null;
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  
  if (year < 1900) return null;
  
  const birthDate = new Date(dobString + 'T00:00:00');
  if (isNaN(birthDate.getTime())) return null;

  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() + 1 !== month ||
    birthDate.getDate() !== day
  ) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/** Format a 'YYYY-MM-DD' string to 'Jun 15, 2025'. */
export function fmtDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/* ========================== Money helpers ========================== */

/** Rounded dollar amount, e.g. '$1,234'. */
export function money0(amount) {
  return '$' + Math.round(amount).toLocaleString('en-US');
}

/** Dollar amount with cents, e.g. '$12.50'. */
export function money(amount) {
  return '$' + amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ========================== Card formatting ========================== */

/** Group digits into blocks of four: '1234123412341234' → '1234 1234 1234 1234'. */
export function formatCard(digits) {
  return (digits.match(/.{1,4}/g) || []).join(' ');
}

/* ========================== Phone helpers ========================== */

const PHONE_PATTERNS = {
  US: '(###) ###-####',
  CA: '(###) ###-####',
  GB: '#### ######',
  IE: '## #######',
  FR: '# ## ## ## ##',
  DE: '### #######',
  AU: '### ### ###',
  NZ: '## ### ####',
  MX: '## #### ####',
  BR: '## #####-####',
  IN: '##### #####',
  JP: '##-####-####',
  ZA: '## ### ####',
};

const DEFAULT_PATTERN = '###########';

/** Get the formatting pattern for a country ISO code. */
export function phonePattern(iso) {
  return PHONE_PATTERNS[iso] || DEFAULT_PATTERN;
}

/** Format raw digits according to the country pattern. */
export function formatPhone(iso, digits) {
  const pattern = phonePattern(iso);
  let formatted = '';
  let digitIndex = 0;
  for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
    formatted += pattern[i] === '#' ? digits[digitIndex++] : pattern[i];
  }
  return formatted;
}

/** Maximum number of digits allowed for a country's phone format. */
export function maxDigits(iso) {
  return (phonePattern(iso).match(/#/g) || []).length;
}

/** Placeholder string for a country's phone format (e.g. '(000) 000-0000'). */
export function placeholderFor(iso) {
  return phonePattern(iso).replace(/#/g, '0');
}

/** Convert a 2-letter ISO code to its flag emoji. */
export function flagFor(iso) {
  if (!iso || iso.length !== 2) return '';
  const REGIONAL_INDICATOR_A = 0x1f1e6;
  return (
    String.fromCodePoint(REGIONAL_INDICATOR_A + iso.charCodeAt(0) - 65) +
    String.fromCodePoint(REGIONAL_INDICATOR_A + iso.charCodeAt(1) - 65)
  );
}

/* ========================== URL param parsing ========================== */

/**
 * Parse a base64-encoded query string into a key-value object.
 * Returns null if the string is not valid encoded params.
 */
export function parseEncodedParams(searchStr) {
  if (!searchStr || searchStr.length <= 1) return null;
  try {
    const decoded = atob(searchStr.substring(1));
    if (!decoded || !decoded.includes('=')) return null;

    const params = {};
    decoded.split('&').forEach(function (part) {
      const kv = part.split('=');
      if (kv.length === 2) {
        params[kv[0]] = decodeURIComponent(kv[1]);
      }
    });
    return params;
  } catch (err) {
    return null;
  }
}

/**
 * Calculate the inclusive number of days between two 'YYYY-MM-DD' strings.
 * Returns 0 if either date is missing or invalid.
 */
export function inclusiveDaysBetween(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const diff = Math.round(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000
  ) + 1;
  return diff > 0 ? diff : 0;
}

/* ========================== Phone validation (libphonenumber) ========================== */

/**
 * Validate a phone number against the currently selected country.
 * @param {string} phoneValue – The full phone value from react-phone-input-2 (digits only, includes dial code).
 * @param {string} countryIso2 – 2-letter ISO code (uppercase), e.g. 'US', 'RO', 'CA'.
 * @returns {boolean} true if the number is valid for the given country.
 */
export function validatePhoneForCountry(phoneValue, countryIso2) {
  if (!phoneValue || !countryIso2) return false;
  try {
    // react-phone-input-2 gives us digits only (with dial code) — prefix with '+' for parsing
    const raw = String(phoneValue).replace(/\D/g, '');
    if (raw.length < 4) return false;
    const number = phoneUtil.parse('+' + raw, countryIso2.toUpperCase());
    // Check the number is valid AND its resolved region matches the selected country.
    // This prevents e.g. a Romanian number from being accepted under US country selection.
    if (!phoneUtil.isValidNumber(number)) return false;
    const regionCode = phoneUtil.getRegionCodeForNumber(number);
    // For NANP countries (US/CA/PR/etc.) that share dial code +1, accept any NANP region
    // when the user has selected a NANP country. Otherwise require exact match.
    const nanpCountries = ['US', 'CA', 'PR', 'VI', 'GU', 'AS', 'MP', 'JM', 'BS', 'BB', 'AG', 'DM', 'GD', 'KN', 'LC', 'VC', 'TT', 'DO'];
    const selectedUpper = countryIso2.toUpperCase();
    if (nanpCountries.includes(selectedUpper) && nanpCountries.includes(regionCode)) {
      return true;
    }
    return regionCode === selectedUpper;
  } catch (e) {
    return false;
  }
}

/* ========================== Validation ========================== */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
  return EMAIL_REGEX.test(email);
}
